import React, { useEffect, useState, useMemo } from 'react'
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom'

import { PageSkeleton } from '_components/page-skeletons'
import { usePermissions } from '_stores/permissions.store'
import { isBrandReportsPath, useBrandReportsAccess } from '_hooks/use-brand-reports-access.hooks'
import useSiteStore from '_stores/site.store'
import { ROUTES } from '../../app.constants'
import { useQuery } from '@hapana/shared/web/services/gqty'
import type { CorporatePermissionsKey } from '@hapana/shared/schemas/users/enums/corporate-permissions'
import type { Site } from '@hapana/shared/web/services/gqty'

interface NavigationGuardState {
  isLoading: boolean
  shouldRedirect: boolean
  redirectPath: string | null
  decidedForPath: string | null
}

const SKIP_GUARD_PATHS = [
  ROUTES.login.path,
  ROUTES.logout.path,
  ROUTES.locationSelection.path,
  ROUTES.userProfile.path,
  ROUTES.siteTeamAccess.path,
  ROUTES.zapierConnect.path,
  ROUTES.reports.children.equipmentCompliance.path,
]

const isCorporatePath = (path: string) => path === '/c' || path.startsWith('/c/')

const NavigationGuard: React.FC = () => {
  const location = useLocation()
  const { siteId: urlSiteId } = useParams<{ siteId?: string }>()

  const shouldSkipGuard = SKIP_GUARD_PATHS.some((path) => location.pathname.includes(path))

  const [state, setState] = useState<NavigationGuardState>({
    isLoading: !shouldSkipGuard,
    shouldRedirect: false,
    redirectPath: null,
    decidedForPath: null,
  })

  const {
    hasPermission,
    getAccessibleSites,
    isLoading: isPermissionsLoading,
    hasUniversalSiteAccess,
    meData,
    parsedPermissions,
  } = usePermissions()

  const {
    currentSite,
    setCurrentSite,
    clearCurrentSite,
    hasExplicitBrandIntent,
    clearBrandIntent,
    _hasHydrated,
  } = useSiteStore()

  const hasUserData = !!meData?.me

  const { hasBrandReportsAccess } = useBrandReportsAccess()

  const hasBrandAccess = useMemo(() => {
    if (!hasUserData) {
      return false
    }

    const readControls: CorporatePermissionsKey = 'read_controls'
    const writeControls: CorporatePermissionsKey = 'write_controls'

    return hasPermission(readControls) || hasPermission(writeControls)
  }, [hasUserData, parsedPermissions])

  // Content Suite is entitled by content__read on its own — it does not require
  // brand (Admin View) access. Without this, removing Admin View from a
  // content-only user evicts them off /c/* onto a 404 on refresh (MEM-298).
  const hasContentRead = useMemo(() => {
    if (!hasUserData) {
      return false
    }
    const contentRead: CorporatePermissionsKey = 'content__read'
    return hasPermission(contentRead)
  }, [hasUserData, parsedPermissions])

  const accessibleSiteIds = useMemo(() => {
    return hasUserData ? getAccessibleSites() : []
  }, [hasUserData, parsedPermissions])

  const hasUniversalAccess = useMemo(() => {
    return hasUserData ? hasUniversalSiteAccess() : false
  }, [hasUserData, parsedPermissions])

  // PERFORMANCE OPTIMIZATION: Only fetch sites if we actually need site objects
  // Users with brand access don't need sites data for navigation decisions
  // Users with a currentSite already set don't need to fetch (they can navigate freely)
  // Users with siteId in URL will have their site set by the URL handler
  const needsSitesForNavigation = useMemo(() => {
    // Brand access users: no sites needed (unless URL has a siteId that needs validation)
    if (hasBrandAccess && !urlSiteId) {
      return false
    }
    // No user data yet: wait for permissions
    if (!hasUserData) {
      return false
    }
    // No accessible sites: no need to fetch
    if (!accessibleSiteIds || accessibleSiteIds.length === 0) {
      return false
    }
    // Universal access ("*"): need to fetch to know site count
    if (hasUniversalAccess) {
      return true
    }
    // Have a siteId in URL AND current site already has full data for it: no fetch needed
    if (urlSiteId && currentSite?.siteId === urlSiteId && currentSite?.name) {
      return false
    }
    // Have a siteId in URL but no full site data yet: need sites to validate and set
    if (urlSiteId) {
      return true
    }
    // Already have a current site set: no blocking fetch needed
    if (currentSite) {
      return false
    }
    // Single site user: need to auto-select, but we can create a minimal site object
    if (accessibleSiteIds.length === 1) {
      return false
    }
    // Multiple sites, no current site: redirect to location selection (no fetch needed)
    if (accessibleSiteIds.length > 1) {
      return false
    }
    return false
  }, [hasBrandAccess, hasUserData, accessibleSiteIds, hasUniversalAccess, currentSite, urlSiteId])

  const query = useQuery({ suspense: false })
  const shouldFetchSites = needsSitesForNavigation

  const sitesResult = shouldFetchSites ? query.sites() : null

  const allSites = sitesResult?.results?.map((result) => result.row) || []
  const isSitesLoading = shouldFetchSites ? query.$state.isLoading : false
  const sitesQueryError = shouldFetchSites ? query.$state.error : undefined

  // Select every field the guard reads in the SAME GQty query. The guard needs `name` to set
  // the current site for a deep link, but only `siteId` was touched here, so the first fetch
  // came back without names and the later `urlSite?.name` read never produced a follow-up
  // fetch: `urlSiteName` stayed undefined and the effect below parked on the skeleton forever
  // (every `/s/:siteId/...` link opened without a selected site hung on KX Pilates).
  if (shouldFetchSites && allSites.length > 0) {
    allSites.forEach((site) => {
      if (site) {
        const fields = [site.siteId, site.name]
        void fields
      }
    })
  }

  const accessibleSites = useMemo(() => {
    if (!allSites || allSites.length === 0) {
      return []
    }
    return hasUniversalAccess
      ? allSites
      : allSites.filter((site) => site.siteId && accessibleSiteIds.includes(site.siteId))
  }, [hasUniversalAccess, allSites, accessibleSiteIds])

  const urlSite =
    urlSiteId && accessibleSites.length > 0
      ? accessibleSites.find((site: Site) => site.siteId === urlSiteId)
      : undefined
  const urlSiteName = urlSite?.name

  // Check if sites are still being loaded or have incomplete data
  // hasSiteData is true if: no sites exist (empty array after loading) OR all sites have valid siteId
  const sitesQueryCompleted = shouldFetchSites ? !isSitesLoading : true
  const hasSiteData =
    sitesQueryCompleted &&
    (accessibleSites.length === 0 || accessibleSites.every((site) => site.siteId))

  // PERFORMANCE: Determine if we're ready to make navigation decisions
  // For brand access users, we don't need sites data at all
  // For others, we use site IDs from permissions when possible
  const isDataLoading = useMemo(() => {
    if (isPermissionsLoading) {
      return true
    }
    if (!hasUserData) {
      return true
    }
    // Brand access users: ready immediately after permissions (unless we need sites for URL validation)
    if (hasBrandAccess && !shouldFetchSites) {
      return false
    }
    // If we need sites data and it's still loading
    if (shouldFetchSites && !hasSiteData) {
      return true
    }
    return false
  }, [isPermissionsLoading, hasUserData, hasBrandAccess, shouldFetchSites, hasSiteData])

  useEffect(() => {
    let isMounted = true
    const cleanup = () => {
      isMounted = false
    }

    const currentPath = location.pathname
    const skipPaths = [ROUTES.login.path, ROUTES.logout.path, ROUTES.locationSelection.path]

    // Check skipPaths FIRST before waiting for data to load
    // This prevents getting stuck in loading state on these pages
    if (skipPaths.some((path) => currentPath.includes(path))) {
      if (isMounted) {
        setState({
          isLoading: false,
          shouldRedirect: false,
          redirectPath: null,
          decidedForPath: currentPath,
        })
      }
      return cleanup
    }

    if (isDataLoading || !_hasHydrated) {
      if (isMounted) {
        setState((prev) => ({ ...prev, isLoading: true }))
      }
      return cleanup
    }

    // If the sites query failed with an error, redirect to location selection
    // This handles cases like "site not found" errors from the backend
    if (sitesQueryError && urlSiteId) {
      if (isMounted) {
        clearCurrentSite()
        setState({
          isLoading: false,
          shouldRedirect: true,
          redirectPath: ROUTES.locationSelection.path,
          decidedForPath: currentPath,
        })
      }
      return cleanup
    }

    // PERFORMANCE OPTIMIZATION: Use site count from permissions for navigation decisions
    // This allows us to make routing decisions without waiting for full site data
    const siteCount = hasUniversalAccess
      ? accessibleSites.length || 999 // Universal access: use fetched count or assume many
      : accessibleSiteIds.length

    // Handle URL siteId - ensure full site data is set before rendering
    if (urlSiteId) {
      // If current site already has full data for this siteId, proceed immediately
      if (currentSite?.siteId === urlSiteId && currentSite?.name) {
        // Already set correctly from location selection - continue to navigation logic
      } else if (!hasSiteData || (accessibleSites.length === 0 && accessibleSiteIds.length > 0)) {
        // Sites data still loading or query returned empty but we expect sites (race condition)
        // Keep loading state to prevent rendering with incomplete data
        if (isMounted) {
          setState((prev) => ({ ...prev, isLoading: true }))
        }
        return cleanup
      } else if (accessibleSites.length > 0) {
        if (urlSite) {
          if (!urlSiteName) {
            if (isMounted) {
              setState((prev) => ({ ...prev, isLoading: true }))
            }
            return cleanup
          }
          if (isMounted) {
            setCurrentSite({ siteId: urlSiteId, name: urlSiteName })
          }
        } else {
          // Site explicitly not in accessible sites - redirect to location selection
          if (isMounted) {
            clearCurrentSite()
            setState({
              isLoading: false,
              shouldRedirect: true,
              redirectPath: ROUTES.locationSelection.path,
              decidedForPath: currentPath,
            })
          }
          return cleanup
        }
      } else {
        // No accessible sites at all - redirect to location selection
        if (isMounted) {
          clearCurrentSite()
          setState({
            isLoading: false,
            shouldRedirect: true,
            redirectPath: ROUTES.locationSelection.path,
            decidedForPath: currentPath,
          })
        }
        return cleanup
      }
    }

    // Handle case where currentSite exists in storage but URL doesn't reflect it
    // Redirect root-level routes to /s/:siteId when a site is selected
    if (!urlSiteId && currentSite?.siteId && !currentPath.startsWith('/c')) {
      // Don't redirect if already on a utility route that doesn't need site prefix
      const utilityRoutes = [ROUTES.locationSelection.path, ROUTES.login.path, ROUTES.logout.path]
      const isUtilityRoute = utilityRoutes.some((path) => currentPath.includes(path))

      if (!isUtilityRoute) {
        // Build the new path with site prefix
        const pathWithoutLeadingSlash = currentPath.startsWith('/')
          ? currentPath.slice(1)
          : currentPath
        const newPath = `/s/${currentSite.siteId}/${pathWithoutLeadingSlash}`

        if (isMounted) {
          setState({
            isLoading: false,
            shouldRedirect: true,
            redirectPath: newPath,
            decidedForPath: currentPath,
          })
        }
        return cleanup
      }
    }

    // PERFORMANCE: For single-site users without currentSite, create minimal site object
    // This avoids waiting for the full sites query.
    // Applies to brand-access users too — single-site admins still need a site in context
    // for site-scoped features (e.g. schedule template instructor list).
    // Never on corporate routes: Layout clears currentSite on /c/*, so selecting one here
    // ping-pongs with it until React throws #185 (OC-1482).
    if (
      siteCount === 1 &&
      !currentSite &&
      !urlSiteId &&
      !isCorporatePath(currentPath) &&
      accessibleSiteIds[0] &&
      !hasExplicitBrandIntent
    ) {
      const singleSiteId = accessibleSiteIds[0]
      // Create a minimal site object - full details will be fetched by components that need them
      const minimalSite = { siteId: singleSiteId } as Site
      if (isMounted) {
        setCurrentSite(minimalSite)
        clearBrandIntent()
      }

      if (currentPath === '/' || currentPath === `/${ROUTES.dashboard.path}`) {
        if (isMounted) {
          setState({
            isLoading: false,
            shouldRedirect: true,
            redirectPath: `/${ROUTES.settings.path}`,
            decidedForPath: currentPath,
          })
        }
        return cleanup
      }
    }

    const navigationResult = determineNavigationFast({
      hasBrandAccess,
      hasBrandReportsAccess,
      hasContentRead,
      siteCount,
      currentSite,
      currentPath,
      hasExplicitBrandIntent,
    })

    if (isMounted) {
      if (navigationResult.shouldClearSite) {
        clearCurrentSite()
      }

      // Validate current site is still accessible (only if we have site data)
      if (
        currentSite &&
        accessibleSites.length > 0 &&
        !isSitesLoading &&
        hasSiteData &&
        !accessibleSites.some((site: Site) => site.siteId === currentSite.siteId)
      ) {
        clearCurrentSite()
      }

      setState({
        isLoading: false,
        shouldRedirect: navigationResult.shouldRedirect,
        redirectPath: navigationResult.redirectPath,
        decidedForPath: currentPath,
      })
    }

    return cleanup
  }, [
    isDataLoading,
    _hasHydrated,
    location.pathname,
    hasBrandAccess,
    hasBrandReportsAccess,
    hasContentRead,
    accessibleSiteIds?.length,
    accessibleSites?.length,
    currentSite?.siteId,
    currentSite?.name,
    hasExplicitBrandIntent,
    hasSiteData,
    isSitesLoading,
    hasUniversalAccess,
    urlSiteId,
    urlSiteName,
    sitesQueryError,
  ])

  // Skip guard entirely for certain paths - don't wait for data loading
  if (shouldSkipGuard) {
    return <Outlet />
  }

  if (state.isLoading) {
    return <PageSkeleton />
  }

  if (state.shouldRedirect && state.redirectPath && state.decidedForPath === location.pathname) {
    return <Navigate to={state.redirectPath} replace />
  }

  return <Outlet />
}

// PERFORMANCE OPTIMIZED: Uses site count instead of full site objects
// This allows navigation decisions without waiting for the sites query
interface NavigationParamsFast {
  hasBrandAccess: boolean
  hasBrandReportsAccess: boolean
  hasContentRead: boolean
  siteCount: number
  currentSite: Site | null
  currentPath: string
  hasExplicitBrandIntent: boolean
}

interface NavigationResult {
  shouldRedirect: boolean
  redirectPath: string | null
  shouldClearSite?: boolean
}

// Priority Rule: User explicitly chose brand navigation AND has brand access → Allow free navigation
// Rule 1: Has brand access → Allow free navigation (don't auto-select anything)
// Rule 0: No brand access + on /c/* path → Redirect to location selection
// Rule 2: No brand access + no locations → Location selection (empty state)
// Rule 3: No brand access + 1 location → Auto-select handled separately (minimal site object)
// Rule 4: No brand access + multiple locations + no site selected → Location selection
// Rule 5: Allow all other navigation
function determineNavigationFast({
  hasBrandAccess,
  hasBrandReportsAccess,
  hasContentRead,
  siteCount,
  currentSite,
  currentPath,
  hasExplicitBrandIntent,
}: NavigationParamsFast): NavigationResult {
  // Only honor explicit brand intent if user actually has brand access
  if (hasExplicitBrandIntent && hasBrandAccess) {
    return { shouldRedirect: false, redirectPath: null }
  }

  if (hasBrandAccess) {
    return { shouldRedirect: false, redirectPath: null }
  }

  // Content Suite is reachable with content__read alone (its own entitlement),
  // even without brand access. This must be checked before the generic /c/*
  // eviction below so a content-only user isn't bounced to a 404 (MEM-298).
  const contentSuitePathPrefix = `/c/${ROUTES.contentSuite.path}`
  if (hasContentRead && currentPath.startsWith(contentSuitePathPrefix)) {
    return { shouldRedirect: false, redirectPath: null }
  }

  if (
    hasBrandReportsAccess &&
    isBrandReportsPath(currentPath) &&
    (isCorporatePath(currentPath) || hasExplicitBrandIntent)
  ) {
    return { shouldRedirect: false, redirectPath: null }
  }

  // Corporate routes (/c/*) require brand access — redirect non-brand users to location selection
  if (isCorporatePath(currentPath)) {
    return {
      shouldRedirect: true,
      redirectPath: ROUTES.locationSelection.path,
    }
  }

  if (siteCount === 0) {
    return {
      shouldRedirect: true,
      redirectPath: ROUTES.locationSelection.path,
      shouldClearSite: true,
    }
  }

  // Single site case is now handled directly in the useEffect
  // to create minimal site object and redirect

  if (siteCount > 1 && !hasBrandAccess && !currentSite) {
    const siteSpecificPaths = [ROUTES.settings.path]

    if (currentPath === '/' || siteSpecificPaths.some((path) => currentPath.includes(path))) {
      // Store the intended path so we can redirect after site selection
      const redirectAfterSelection = currentPath !== '/' ? currentPath : null
      const locationSelectionPath = redirectAfterSelection
        ? `${ROUTES.locationSelection.path}?redirect=${encodeURIComponent(redirectAfterSelection)}`
        : ROUTES.locationSelection.path

      return {
        shouldRedirect: true,
        redirectPath: locationSelectionPath,
      }
    }
  }

  return { shouldRedirect: false, redirectPath: null }
}

export default NavigationGuard
